import { EventColorCategory } from "@clockwise/client-commons/src/util/event-category-coloring";
import { createTagForMutation, Tags } from "@clockwise/client-commons/src/util/event-tag";
import { isImpersonated } from "@clockwise/client-commons/src/util/jwt";
import {
  ConferencingType,
  EventPermissions,
  RecurrenceIntervalTypeEnum,
  RepeatingEventSaveOption,
  ResponseStatusEnum,
} from "@clockwise/schema";
import { EventId, useUpdateActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
import { generateRescheduleCQL } from "@clockwise/web-commons/src/util/generateCQL";
import { jwt } from "@clockwise/web-commons/src/util/local-storage";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { isEqual, noop } from "lodash";
import { DateTime, Interval } from "luxon";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useBoolean } from "usehooks-ts";
import { useUserConfrencingTypes } from "../../hooks/useUserConfrencingTypes";
import { clearAllSelected as clearAllSelectedCalendarsInMultiCalendar } from "../../state/actions/multi-calendar.actions";
import { logger } from "../../util/logger.util";
import { useWorkingHours } from "../account-preferences-working-meeting-hours/hooks/useWorkingHours";
import { useResetEditsToAttendees as useResetEditsToAttendeesForMultiCalendar } from "../chat/ai-chat/hooks/multicalendar/event-card-edit/useResetEditsToAttendees";
import { useSetOriginalAttendeesBeforeEdit as useSetOriginalAttendeesBeforeEditForMultiCalendar } from "../chat/ai-chat/hooks/multicalendar/event-card-edit/useSetOriginalAttendeesBeforeEdit";
import { useProcessMessage } from "../chat/ai-chat/hooks/useProcessMessage";
import { shouldUpdateThisAndFutureEvents } from "../chat/ai-chat/utils/getRecurrenceToSave";
import useOrgDomains from "../hooks/useOrgDomains";
import useEditExistingEvent from "../web-app-calendar/hooks/useEditExistingEvent";
import {
  formatAttendeesToSaveToEvent,
  formatFlexUpdates,
  formatRecurrenceToSave,
  formatRecurrenceToString,
  formatSparkleInTitle,
  formatVCToSave,
  getBodyForSaveChanges,
  getDisabledSaveOptions,
  isAllDayBasedOnTimes,
} from "../web-app-calendar/hooks/useEditExistingEvent.util";
import useEventDetails, {
  CategoryOption,
  EventDetails,
} from "../web-app-calendar/hooks/useEventDetails";
import { Divider } from "./atoms/Divider";
import { WarningMessage } from "./atoms/WarningMessage";
import { EventCardExpanded } from "./EventCardExpanded";
import { useEventAttendees } from "./hooks/EventAttendeesContext";
import { EventCardContextProvider, useReadEventTimeDetails } from "./hooks/EventCardContext";
import { ECAgenda } from "./molecules/ECAgenda";
import { ECAttendeeWrapper } from "./molecules/ECAttendeesV2/ECAttendeeWrapper";
import { ECCalendarInfo } from "./molecules/ECCalendarInfo";
import { ECFlexibility } from "./molecules/ECFlexibility";
import { ECFlexSettings } from "./molecules/ECFlexSettings";
import { ECFooter } from "./molecules/ECFooter";
import { ECPrivateEventCallout } from "./molecules/ECPrivateEventCallout";
import { Recurrence, SelectableRecurrenceOption } from "./molecules/ECRecurrence";
import { ECResponseStatus } from "./molecules/ECResponseStatus";
import { ECRoomAndLocation } from "./molecules/ECRoomAndLocation";
import { Time } from "./molecules/ECTime";
import { ECTitle } from "./molecules/ECTitle";
import { ECVideoConf } from "./molecules/ECVideoConf";
import { EMPTY_FLEX_DETAILS, EventCardFlex, EventCardMode } from "./types";
import { getEventCardWarning } from "./utils/getEventCardWarning";
import { getEventMinMaxConstraints } from "./utils/getEventMinMaxConstraints";
import { getListOfNonRemovableAttendeeIds } from "./utils/getListOfNonRemovableAttendeeIds";
import { getDeleteEventCopy, getSubmitButtonCopy } from "./utils/getSubmitButtonCopy";
import {
  canModifyWholeEvent,
  canUserInviteAttendees,
  canUserRSVP,
  isWaitingOnAsync,
} from "./utils/getUserEventPermissions.util";

// Temporary constant to show save button
const IS_SENDING_TOO = false;

const DEFAULT_START_ISO = DateTime.now().setZone(getRenderTimeZone());
const DEFAULT_END_ISO = DateTime.now().setZone(getRenderTimeZone()).plus({ hours: 1 });

const toDateTime = (iso?: string): DateTime | undefined =>
  iso ? DateTime.fromISO(iso, { zone: getRenderTimeZone() }) : undefined;

export const EventCardWrapper = ({ activeEvent }: { activeEvent: EventId }) => {
  const { eventDetails, colorSettings, loading, error: fetchedDetailsError } = useEventDetails(
    activeEvent.externalEventId,
    activeEvent.calendarId,
  );

  const isAllDayFromTimes = isAllDayBasedOnTimes(
    toDateTime(eventDetails.startISO),
    toDateTime(eventDetails.endISO),
  );

  const contextValue = {
    startTime: toDateTime(eventDetails.startISO) ?? DEFAULT_START_ISO,
    endTime: toDateTime(eventDetails.endISO) ?? DEFAULT_END_ISO,
    isAllDay: isAllDayFromTimes,
    timeError: null,
    attendees: eventDetails.attendeePeople,
  };

  // give a key to the context provider, so we ensure the provider is recreated once we have data
  //  this is a bit of a hack because we need to provide some initial data before information is fetched,
  //  because `EventCard` needs data at all times
  return (
    <EventCardContextProvider key={eventDetails.id} initValue={contextValue}>
      <EventCard
        eventDetails={eventDetails}
        loading={loading}
        eventId={activeEvent}
        error={Boolean(fetchedDetailsError)}
        colorSettings={colorSettings}
      />
    </EventCardContextProvider>
  );
};

// render all the event card pieces, the parent component is responsible for fetching data/managing context
const EventCard: React.FC<{
  eventDetails: EventDetails;
  loading: boolean;
  eventId: EventId;
  error?: boolean;
  colorSettings?: CategoryOption[];
}> = ({ eventDetails, loading, eventId, error, colorSettings }) => {
  const dispatch = useDispatch();
  const ecosystem = useEcosystem();

  const [lastFetchedEventId, setLastFetchedEventId] = useState<string | undefined>(undefined);
  const [viewMode, setViewMode] = useState<EventCardMode>(EventCardMode.default);
  const [eventName, setEventName] = useState<string>("");
  const [agenda, setAgenda] = useState<string>("");
  const [responseStatus, setResponseStatus] = useState<ResponseStatusEnum>(
    ResponseStatusEnum.NotApplicable,
  );
  const [eventRecurrence, setEventRecurrence] = useState<SelectableRecurrenceOption | string>(
    "None",
  );
  const [flexDetails, setFlexDetails] = useState<EventCardFlex>(EMPTY_FLEX_DETAILS);
  const [location, setLocation] = useState<string | undefined>(undefined);
  const [videoType, setVideoType] = useState<ConferencingType | undefined>(undefined);
  const [videoLink, setVideoLink] = useState<string>("");
  const [category, setCategory] = useState<EventColorCategory>(EventColorCategory.AdHoc);
  const { domains: orgDomains } = useOrgDomains();
  const { conferencingTypes } = useUserConfrencingTypes();
  const updateActiveEvent = useUpdateActiveEvent();
  const { workingHours } = useWorkingHours();
  const { processMessage } = useProcessMessage();
  const {
    value: agendaHasChanges,
    setTrue: setAgendaChanges,
    setFalse: setAgendaNoChanges,
  } = useBoolean(false);

  const { startTime, endTime, timesHaveChanged } = useReadEventTimeDetails();
  const { attendees: attendeesInEvent, attendeesChanged } = useEventAttendees();

  const { externalEventId, calendarId } = eventId;

  const handleFlexChange = (flexible: boolean) => {
    setFlexDetails({
      ...flexDetails,
      isFlexible: flexible,
    });
    setEventName(formatSparkleInTitle(eventName, flexible));
  };

  const closeEventCard = () => {
    updateActiveEvent(null);
  };
  const {
    id: fetchedEventId,
    calendarId: fetchedCalendarId,
    attendeePeople: fetchedAttendees,
    description: fetchedDescription,
    flexibilityDescription,
    flexibilityStatus,
    flexDetails: fetchedFlexDetails,
    flexConstraints,
    location: fetchedLocation,
    responseStatus: fetchedResponseStatus,
    title: fetchedTitle,
    videoLink: fetchedVideoLink,
    videoType: fetchedVideoType,
    startISO: fetchedStartISO,
    eventPermissions: fetchedEventPermissions,
    organizerCalId: fetchedOrganizerCalId,
    attendeesOmitted: fetchedAttendeesOmitted,
    descriptionOmitted: fetchedDescriptionOmitted,
    recurrence: fetchedRecurrence,
    category: fetchedCategory,
    locked: fetchedLocked,
  } = eventDetails;
  const customRecurrenceString = formatRecurrenceToString(fetchedRecurrence);
  const hasFlexBeenModified = !isEqual(fetchedFlexDetails, flexDetails);

  const disabledRecurrenceSaveOptions = getDisabledSaveOptions({
    hasFlexBeenModified: hasFlexBeenModified,
    hasRecurrenceBeenModified: customRecurrenceString !== eventRecurrence,
    isDescriptionOmitted: !!fetchedDescriptionOmitted,
  });

  const submitRescheduleMessage = () => {
    if (!fetchedStartISO || !fetchedTitle) return;
    const zonedStartISO = DateTime.fromISO(fetchedStartISO).setZone(getRenderTimeZone()).toISO();
    const cql = generateRescheduleCQL(externalEventId);
    void processMessage(
      `Reschedule ${fetchedTitle}`,
      {
        eventMentions: [{ externalEventId, startTime: zonedStartISO, title: fetchedTitle }],
        personMentions: [],
      },
      cql,
    );
    closeEventCard();
  };

  const { onSaveEventChanges, savingChanges, onDeleteEvent, deletingEvent } = useEditExistingEvent(
    externalEventId,
    calendarId,
    Boolean(fetchedRecurrence?.intervalType !== RecurrenceIntervalTypeEnum.NONE),
    {
      refetchQueriesOnEdit: ["CalendarEvents"],
      onEditCompleted: () => {
        dispatch(clearAllSelectedCalendarsInMultiCalendar());
        toast.success("Event updated successfully!");
        closeEventCard();
      },
      onEditError: (err) => {
        toast.error("Failed to update event.");
        logger.error("unabled to edit event details for event", {
          error: err || "unabled to edit event details for event",
          externalEventId,
          calendarId,
        });
      },
      refetchQueriesOnDelete: ["CalendarEvents"],
      onDeletionCompleted: () => {
        dispatch(clearAllSelectedCalendarsInMultiCalendar());
        if (fetchedEventPermissions === EventPermissions.ALL) {
          toast.success("Event deleted.");
        } else {
          toast.success("Event removed.");
        }
        closeEventCard();
      },
      onDeletionError: (err) => {
        toast.error("Failed to update event.");
        logger.error("unabled to delete event", {
          error: err || "unabled to delete event",
          externalEventId,
          calendarId,
        });
      },
    },
  );

  const isImpersonatedUser = isImpersonated(jwt.get());

  const formatAndSubmitChanges = async (saveOption: RepeatingEventSaveOption) => {
    if (!fetchedOrganizerCalId || !fetchedEventId) {
      toast.error("Unable to save changes. Please try again later.");
      return;
    }
    const inputFlexUpdates = formatFlexUpdates(flexDetails, fetchedEventId);
    const attendees = fetchedAttendeesOmitted
      ? undefined
      : formatAttendeesToSaveToEvent(attendeesInEvent, responseStatus);
    const timeAsInterval = Interval.fromISO(`${startTime}/${endTime}`, {
      zone: getRenderTimeZone(),
    }).toISO();

    const body = getBodyForSaveChanges(
      {
        attendees,
        description: agenda,
        isFlexible: hasFlexBeenModified ? flexDetails.isFlexible : null,
        location,
        flexUpdates: hasFlexBeenModified ? inputFlexUpdates : null,
        recurrence: formatRecurrenceToSave(eventRecurrence, startTime),
        responseStatus,
        sendNotifications: IS_SENDING_TOO,
        time: timeAsInterval,
        title: eventName,
        conferenceType: formatVCToSave(videoType, fetchedVideoType),
        repeatingEventSaveOption: shouldUpdateThisAndFutureEvents(
          saveOption,
          fetchedRecurrence,
          eventRecurrence,
        ),
        category: createTagForMutation(Tags.EventColoringCategory, category),
      },

      fetchedEventPermissions,
    );

    await onSaveEventChanges?.(body);
  };

  const resetCardToFetchedValues = () => {
    setEventName(fetchedTitle || "");
    setAgenda(fetchedDescription || "");
    setResponseStatus(fetchedResponseStatus || ResponseStatusEnum.NotApplicable);
    setFlexDetails(fetchedFlexDetails);
    setLocation(fetchedLocation);
    setVideoType(fetchedVideoType);
    setEventRecurrence(customRecurrenceString);
    setVideoLink(fetchedVideoLink || "");
    setLastFetchedEventId(fetchedEventId);
    setCategory(fetchedCategory || EventColorCategory.AdHoc);
    setAgendaNoChanges();
  };

  useEffect(() => {
    if (fetchedEventId && fetchedEventId !== lastFetchedEventId) {
      resetCardToFetchedValues();
    }
  }, [fetchedEventId]);

  const warningMessage = getEventCardWarning(orgDomains, attendeesInEvent, fetchedEventPermissions);

  const isWaiting = isWaitingOnAsync(loading, savingChanges, deletingEvent);
  const readOnly = !canModifyWholeEvent(fetchedEventPermissions, isWaiting, fetchedLocked);
  const userIsOnlyAttendee = attendeesInEvent.length <= 1 && !fetchedAttendeesOmitted;

  const attendees = fetchedAttendeesOmitted
    ? undefined
    : formatAttendeesToSaveToEvent(attendeesInEvent, responseStatus);

  useSetOriginalAttendeesBeforeEditForMultiCalendar({
    eventId: externalEventId,
    calendarIds: attendees?.map((attendee) => attendee.calendarId) ?? [],
  });

  const [resetEditsToAttendeesForMultiCalendar] = useResetEditsToAttendeesForMultiCalendar({
    externalEventId,
  });

  const wrappedOnClose = () => {
    resetEditsToAttendeesForMultiCalendar();
    closeEventCard();
  };

  const getChangesCount = (obj: { [key: string]: boolean }) => {
    let count = 0;
    for (const k in obj) {
      if (obj[k]) {
        count++;
      }
    }
    return count;
  };

  const changesMap = {
    eventName: !loading && eventName !== fetchedTitle,
    category: !loading && category !== fetchedCategory,
    agenda: !loading && agendaHasChanges,
    location: !loading && (location || "") !== (fetchedLocation || ""),
    videoType: !loading && videoType !== fetchedVideoType,
    eventRecurrence: !loading && customRecurrenceString !== eventRecurrence,
    flexDetails: !loading && !isEqual(flexDetails, fetchedFlexDetails),
    rsvp: !loading && Boolean(fetchedResponseStatus) && responseStatus !== fetchedResponseStatus,
    time: timesHaveChanged,
    attendees: attendeesChanged,
  };

  const anyPropertyChanged = Object.values(changesMap).some((change) => change);
  const changesCount = getChangesCount(changesMap);

  const organizer = eventDetails.attendeePeople.find(
    (a) => a.primaryCalendar === eventDetails.organizerCalId,
  );

  const isOtherUsersEvent = fetchedEventPermissions === EventPermissions.NONE;

  return (
    <EventCardExpanded
      onClose={wrappedOnClose}
      mode={viewMode}
      loading={isWaiting}
      hasFetchingError={error}
      shouldShowRSVP={canUserRSVP(
        responseStatus,
        fetchedEventPermissions,
        ecosystem,
        (organizer && organizer.isYou) || false,
        loading,
      )}
      changesCount={changesCount}
      flexSettingsView={
        !loading &&
        !readOnly && (
          <ECFlexSettings
            isOpen={viewMode === EventCardMode.flexSettings}
            goBack={() => {
              setViewMode(EventCardMode.default);
            }}
            flexDetails={flexDetails}
            flexStatus={flexibilityStatus || null}
            eventName={eventName}
            onFlexDetailsChange={setFlexDetails}
            availableWorkDays={flexConstraints?.availableMeetingDays || []}
            showMeetingHoursConflict={!loading && !flexConstraints?.availableMeetingHours}
            minMaxRecommendedValues={getEventMinMaxConstraints(
              flexConstraints?.availableMeetingHours || undefined,
              workingHours || undefined,
              startTime,
            )}
            onUnpauseEvent={() => {
              toast.error("Not yet implemented");
            }}
          />
        )
      }
      rsvpModule={
        <ECResponseStatus
          onRSVPChange={setResponseStatus}
          rsvpStatus={responseStatus}
          loading={isWaiting}
        />
      }
      footer={
        <ECFooter
          disabled={isWaiting || isImpersonatedUser}
          onUpdateCancel={onDeleteEvent}
          onSubmit={formatAndSubmitChanges}
          updatesPresent={anyPropertyChanged}
          isCancelled={false}
          permissions={fetchedEventPermissions}
          disabledRecurrenceSaveOptions={disabledRecurrenceSaveOptions}
          showDropdownOnSave={eventRecurrence !== "None"}
          submissionCopy={getSubmitButtonCopy(false, !userIsOnlyAttendee, isImpersonatedUser)}
          onReschedule={fetchedTitle && fetchedStartISO ? submitRescheduleMessage : undefined}
          deletionCopy={getDeleteEventCopy(
            fetchedEventPermissions === EventPermissions.ALL,
            isImpersonatedUser,
          )}
        />
      }
    >
      {!fetchedLocked && warningMessage && !isWaiting && <WarningMessage type={warningMessage} />}
      <ECTitle
        readOnly={readOnly}
        eventName={eventName}
        onEventNameChange={(e) => setEventName(() => e.target.value)}
        loading={isWaiting}
        allColorSettings={isOtherUsersEvent || Boolean(fetchedLocked) ? undefined : colorSettings}
        currentCategory={category}
        onCategoryChange={setCategory}
        locked={fetchedLocked}
      />
      <div className="cw-flex cw-flex-col cw-gap-1">
        {!loading && fetchedCalendarId && isOtherUsersEvent && (
          <>
            <ECCalendarInfo calendarId={fetchedCalendarId} />
            <Divider />
          </>
        )}
        {!fetchedLocked && (
          <>
            <ECFlexibility
              flexibilityDescription={flexibilityDescription}
              flexibilityStatus={
                !loading && !flexConstraints?.availableMeetingHours
                  ? "Stuck"
                  : flexibilityStatus || "Default"
              }
              isFlexible={flexDetails.isFlexible}
              onFlexibilityChange={handleFlexChange}
              onPauseChange={handleFlexChange}
              readOnly={readOnly}
              loading={isWaiting}
              goToFlexDetails={() => {
                setViewMode(EventCardMode.flexSettings);
              }}
              showChanges={changesMap.flexDetails}
            />
            <Divider />
            <ECAttendeeWrapper
              attendeesOmitted={fetchedAttendeesOmitted}
              readOnly={!canUserInviteAttendees(fetchedEventPermissions, isWaiting)}
              loading={isWaiting}
              nonEditableAttendeeIds={getListOfNonRemovableAttendeeIds(
                fetchedEventPermissions,
                fetchedAttendees,
              )}
              externalEventId={externalEventId}
              calendarId={calendarId}
              onReschedule={
                canModifyWholeEvent(fetchedEventPermissions) ? submitRescheduleMessage : undefined
              }
            />
            <Divider />
          </>
        )}
        <Time readOnly={readOnly} loading={isWaiting} timesHaveChanged={timesHaveChanged} />
        {!fetchedLocked && (
          <>
            <Recurrence
              loading={isWaiting}
              readOnly={readOnly}
              fetchedRecurrenceString={customRecurrenceString}
              selectedOption={eventRecurrence}
              onSelect={setEventRecurrence}
              showChanges={changesMap.eventRecurrence}
            />
            <Divider />
            <ECVideoConf
              onSelect={setVideoType}
              readonly={Boolean(readOnly || videoLink)} // We dont want users editing the video link if one already exists (event-cal only)
              selected={videoType}
              videoLink={videoLink}
              enabledTypes={conferencingTypes}
              loading={isWaiting}
              showChanges={changesMap.videoType}
            />
            <ECRoomAndLocation
              location={location}
              onLocationChange={setLocation}
              onRoomSelect={noop}
              readonly={readOnly}
              rooms={undefined}
              loading={isWaiting}
              showChanges={changesMap.location}
            />
            <Divider />
            <ECAgenda
              readOnly={readOnly}
              agenda={agenda}
              onChangeAgenda={(value, userModified) => {
                setAgenda(value);
                if (userModified) {
                  setAgendaChanges();
                }
              }}
              descriptionOmitted={fetchedDescriptionOmitted}
              loading={isWaiting}
              showChanges={changesMap.agenda}
            />
          </>
        )}
        {fetchedLocked && <ECPrivateEventCallout loading={isWaiting} />}
      </div>
    </EventCardExpanded>
  );
};
